import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { objectToCamel, objectToSnake } from 'ts-case-convert';
import { IPromotionalCode, IPromotionalCodeRequest } from 'src/app/models';
import { PromotionalCodesHttpService } from './promotional-codes.http.service';

@Injectable({
  providedIn: 'root'
})
export class PromotionalCodesService {

  constructor(private promotionalCodesHttpService: PromotionalCodesHttpService) { }

  private extractList(data: any): IPromotionalCode[] {
    const inner = data && !Array.isArray(data) && 'data' in data ? data.data : data;
    if (Array.isArray(inner)) return inner;
    if (inner && Array.isArray(inner.content)) return inner.content;
    console.warn('Unexpected promotional codes response shape:', data);
    return [];
  }

  // The paginated list endpoint serializes LocalDateTime as
  // [year, month, day, hour, minute(, second)] because the `Page<>` wrapper
  // bypasses the snake-case converter that has JavaTimeModule registered.
  // Normalize to an ISO string so the UI sees a consistent shape.
  private normalizeDates(code: IPromotionalCode): IPromotionalCode {
    return {
      ...code,
      validFrom: this.toIsoString(code.validFrom as any),
      validUntil: this.toIsoString(code.validUntil as any),
    };
  }

  private toIsoString(value: any): string | null {
    if (value == null) return null;
    if (Array.isArray(value)) {
      const [y, mo = 1, da = 1, hh = 0, mm = 0, ss = 0] = value;
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${y}-${pad(mo)}-${pad(da)}T${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    }
    return String(value);
  }

  async getPromotionalCodes(page?: number, size?: number, code?: string, active?: boolean): Promise<IPromotionalCode[]> {
    const source$ = this.promotionalCodesHttpService.getPromotionalCodes(page, size, code, active);
    const response = await lastValueFrom(source$);
    const data = objectToCamel(response as object) as any;
    return this.extractList(data).map(c => this.normalizeDates(c));
  }

  async getPromotionalCode(id: number): Promise<IPromotionalCode> {
    const source$ = this.promotionalCodesHttpService.getPromotionalCode(id);
    const response = await lastValueFrom(source$);
    return this.normalizeDates(objectToCamel(response as object) as IPromotionalCode);
  }

  async createPromotionalCode(promotionalCode: IPromotionalCodeRequest): Promise<IPromotionalCode> {
    const requestBody = objectToSnake(promotionalCode);
    const source$ = this.promotionalCodesHttpService.createPromotionalCode(requestBody);
    const response = await lastValueFrom(source$);
    return this.normalizeDates(objectToCamel(response as object) as IPromotionalCode);
  }

  async updatePromotionalCode(id: number, promotionalCode: IPromotionalCodeRequest): Promise<IPromotionalCode> {
    const requestBody = objectToSnake(promotionalCode);
    const source$ = this.promotionalCodesHttpService.updatePromotionalCode(id, requestBody);
    const response = await lastValueFrom(source$);
    return this.normalizeDates(objectToCamel(response as object) as IPromotionalCode);
  }

  async deactivatePromotionalCode(id: number): Promise<void> {
    const source$ = this.promotionalCodesHttpService.deactivatePromotionalCode(id);
    await lastValueFrom(source$);
  }

  async activatePromotionalCode(id: number): Promise<void> {
    const source$ = this.promotionalCodesHttpService.activatePromotionalCode(id);
    await lastValueFrom(source$);
  }
}
